import {InfiniteData} from '@tanstack/react-query';
import {
  Client,
  ClientUserAssignment,
  Clients,
  IncompleteAdmissionCRFsResponse,
  IncompleteDischargeCRFsResponse,
} from '@/api/dto/dto';

// Screens that render AssignedToClient cache their rows in different shapes: the clients
// list stores Client objects under `clients`, while the CRF reports store report rows that
// wrap the Client under `client`.
export type AssignmentCachePage = {
  data: Partial<Clients & IncompleteAdmissionCRFsResponse & IncompleteDischargeCRFsResponse>;
};

export type AssignmentCache = InfiniteData<AssignmentCachePage>;

function mapRowClients<T extends { client: Client }>(
  rows: T[],
  patientId: string,
  nextClient: (client: Client) => Client,
): { rows: T[]; changed: boolean } {
  let changed = false;

  const next = rows.map((row) => {
    if (row?.client?.patientId !== patientId) {
      return row;
    }

    changed = true;
    return { ...row, client: nextClient(row.client) };
  });

  return { rows: next, changed };
}

/**
 * Applies an assignment change to whichever cache shape the calling screen holds, leaving
 * the cache untouched when the shape is not recognised or the client is absent.
 *
 * Every row for the client is updated, not just the first: the CRF reports return one row
 * per encounter, so a client with multiple encounters occupies several rows, while an
 * assignment applies to the client and roster as a whole.
 *
 * This runs inside the mutation's onMutate, so it must never throw — doing so would abort
 * the mutation before the request is sent.
 */
export function applyAssignmentToCache(
  cache: AssignmentCache | undefined,
  patientId: string,
  updateAssignments: (current: ClientUserAssignment[]) => ClientUserAssignment[],
): AssignmentCache | undefined {
  if (!cache?.pages?.length) {
    return cache;
  }

  const nextClient = (client: Client): Client => ({
    ...client,
    userAssignments: updateAssignments(client?.userAssignments ?? []),
  });

  let changed = false;

  const pages = cache.pages.map((page) => {
    if (!page?.data) {
      return page;
    }

    const { clients, incompleteAdmissionCrfs, incompleteAdmissionAndDischargeCrfs } = page.data;
    const data = { ...page.data };
    let pageChanged = false;

    if (Array.isArray(clients)) {
      let clientsChanged = false;

      const next = clients.map((client) => {
        if (client?.patientId !== patientId) {
          return client;
        }

        clientsChanged = true;
        return nextClient(client);
      });

      if (clientsChanged) {
        data.clients = next;
        pageChanged = true;
      }
    }

    if (Array.isArray(incompleteAdmissionCrfs)) {
      const result = mapRowClients(incompleteAdmissionCrfs, patientId, nextClient);

      if (result.changed) {
        data.incompleteAdmissionCrfs = result.rows;
        pageChanged = true;
      }
    }

    if (Array.isArray(incompleteAdmissionAndDischargeCrfs)) {
      const result = mapRowClients(incompleteAdmissionAndDischargeCrfs, patientId, nextClient);

      if (result.changed) {
        data.incompleteAdmissionAndDischargeCrfs = result.rows;
        pageChanged = true;
      }
    }

    if (!pageChanged) {
      return page;
    }

    changed = true;
    return { ...page, data };
  });

  return changed ? { ...cache, pages } : cache;
}
